/**
 * S6/S7 (multi-provider quote flow) — grouping jobs into "requests" and
 * deriving the customer-facing tab/group each request belongs to.
 *
 * A "request" is one batch (N sibling Job docs sharing a `batchId`, from the
 * quote-broadcast feature) or one lone 1:1 Job (no `batchId`). See
 * ai-work/hp-design/form-scope-and-delivery/README.md (S6) for the design
 * intent — the tab/group rules below are a from-scratch derivation against
 * real ENGAGEMENT_STATUS values, not a port of the design mock's fictional
 * per-request status field (see plan.md items 2/4a for the reasoning).
 */
import { Job } from './type';
import { ENGAGEMENT_STATUS, getQuoteExpiryInfo } from './engagement';

export type SiblingEffectiveState =
  | 'booked'
  | 'cancelled'
  | 'declined'
  | 'quoted-expired'
  | 'quoted-live'
  | 'requested';

/**
 * Per-job effective state. `Expired` is checked defensively (mirrors
 * JobDetail.tsx's `isExpiredStatus`) even though no Phase-2 actor sets it
 * today — a lapsed quote's wire status stays `Quoted` forever, so the real
 * signal is the client-computed countdown via `getQuoteExpiryInfo`.
 */
export const getSiblingEffectiveState = (
  job: Job,
  now: number = Date.now()
): SiblingEffectiveState => {
  const { status } = job;
  if (
    status === ENGAGEMENT_STATUS.ACCEPTED ||
    status === ENGAGEMENT_STATUS.SCHEDULED ||
    status === ENGAGEMENT_STATUS.INPROGRESS ||
    status === ENGAGEMENT_STATUS.AWAITING ||
    status === ENGAGEMENT_STATUS.COMPLETED
  ) {
    return 'booked';
  }
  if (status === ENGAGEMENT_STATUS.CANCELLED) return 'cancelled';
  if (status === ENGAGEMENT_STATUS.DECLINED) return 'declined';
  if (status === ENGAGEMENT_STATUS.EXPIRED) return 'quoted-expired';
  if (status === ENGAGEMENT_STATUS.QUOTED) {
    const expiry = getQuoteExpiryInfo(job.quoteExpiresAt, now);
    return expiry?.isExpired ? 'quoted-expired' : 'quoted-live';
  }
  return 'requested';
};

export type RequestTab = 'active' | 'booked' | 'closed';
export type RequestGroup = 'needs-you' | 'waiting-on-providers' | null;

export interface QuoteRequest {
  /** batchId for a broadcast, else the lone job's jobId. Stable list key. */
  key: string;
  batchId?: string;
  jobs: Job[];
  tab: RequestTab;
  group: RequestGroup;
  /**
   * True only for the "every provider declined" case that deliberately stays
   * in Active/Needs-you (see README S6 + plan.md 4a) rather than Closed.
   * Drives the "Show me" CTA + Dismiss affordance.
   */
  allDeclined: boolean;
  /** Count of siblings with a live (non-expired) quote to review. */
  quotesInCount: number;
  /** The accepted sibling, when tab === 'booked'. */
  bookedJob?: Job;
}

/**
 * Pick which job a request's CTA should navigate to.
 * - Booked → the accepted job.
 * - Otherwise → the most-recently-quoted live quote, if any (most relevant
 *   thing to land on for "View quote" / "View N quotes").
 * - Otherwise → the earliest-created sibling (stable, arbitrary — nothing is
 *   quoted yet so it doesn't matter which one).
 */
export const pickRepresentativeJob = (request: QuoteRequest): Job => {
  if (request.bookedJob) return request.bookedJob;
  const live = request.jobs.filter(
    (j) => getSiblingEffectiveState(j) === 'quoted-live'
  );
  if (live.length > 0) {
    return live.reduce((latest, j) =>
      (j.quotedAt ?? '') > (latest.quotedAt ?? '') ? j : latest
    );
  }
  return request.jobs.reduce((earliest, j) =>
    (j.createdAt ?? '') < (earliest.createdAt ?? '') ? j : earliest
  );
};

const DISMISSED_STORAGE_KEY = 'hp:dismissedRequests';

/**
 * Client-side-only "dismiss" persistence for the all-declined Needs-you
 * card (README doesn't specify a backend field for this, and adding one is
 * out of scope this run — see plan.md 4b). Per-device, not synced across a
 * customer's other browsers/devices.
 */
export const getDismissedRequestKeys = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

export const dismissRequest = (key: string): void => {
  try {
    const current = getDismissedRequestKeys();
    current.add(key);
    window.localStorage.setItem(
      DISMISSED_STORAGE_KEY,
      JSON.stringify([...current])
    );
  } catch {
    // Best-effort only — localStorage may be unavailable (private mode,
    // quota exceeded). A failed dismiss just means the card resurfaces.
  }
};

/**
 * Group a flat job list into requests and derive each one's tab/group.
 * See README S6 + plan.md items 2/4a for the exact rule set.
 */
export const groupJobsIntoRequests = (
  jobs: Job[],
  opts?: { now?: number; dismissedKeys?: Set<string> }
): QuoteRequest[] => {
  const now = opts?.now ?? Date.now();
  const dismissed = opts?.dismissedKeys ?? getDismissedRequestKeys();

  const byKey = new Map<string, Job[]>();
  for (const job of jobs) {
    const key = job.batchId || job.jobId;
    const list = byKey.get(key);
    if (list) list.push(job);
    else byKey.set(key, [job]);
  }

  const requests: QuoteRequest[] = [];
  for (const [key, siblingJobs] of byKey) {
    const states = siblingJobs.map((j) => getSiblingEffectiveState(j, now));
    const bookedIndex = states.findIndex((s) => s === 'booked');

    let tab: RequestTab;
    let group: RequestGroup = null;
    let allDeclined = false;

    if (bookedIndex >= 0) {
      tab = 'booked';
    } else if (states.some((s) => s === 'requested' || s === 'quoted-live')) {
      tab = 'active';
      group = states.some((s) => s === 'quoted-live')
        ? 'needs-you'
        : 'waiting-on-providers';
    } else {
      // Every sibling is terminal: cancelled / declined / quoted-expired.
      const anyCancelled = states.some((s) => s === 'cancelled');
      const everyDeclined = states.every((s) => s === 'declined');
      if (anyCancelled) {
        tab = 'closed';
      } else if (everyDeclined) {
        tab = 'active';
        group = 'needs-you';
        allDeclined = true;
      } else {
        // Any quoted-expired present (alone or mixed with declined).
        tab = 'closed';
      }
    }

    if (dismissed.has(key)) {
      tab = 'closed';
      group = null;
      allDeclined = false;
    }

    requests.push({
      key,
      batchId: siblingJobs[0].batchId,
      jobs: siblingJobs,
      tab,
      group,
      allDeclined,
      quotesInCount: states.filter((s) => s === 'quoted-live').length,
      bookedJob: bookedIndex >= 0 ? siblingJobs[bookedIndex] : undefined,
    });
  }

  return requests;
};
